/**
 * Auth Context
 * Provides authentication state and methods throughout the app
 * Supports both Cognito (production) and local auth (development)
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { User, AuthState } from '../types/auth';
import { AuthContext, type AuthContextType } from './authContextDefinition';
import * as authApi from '../services/authApi';
import { isCognitoConfigured } from '../config/cognito';
import { identifyUser, resetUser, trackLogin, trackRegisterCompleted, getStoredUtmParams } from '../services/analytics';
import { STORAGE_KEYS } from '../constants/storage';


// Token refresh interval (refresh 1 minute before expiry - tokens last 15 minutes)
const TOKEN_REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cognitoMode = isCognitoConfigured();

  // Clear refresh timer
  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Clear auth state
  const clearAuthState = useCallback(() => {
    clearRefreshTimer();
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, [clearRefreshTimer]);

  // Schedule token refresh
  const scheduleTokenRefresh = useCallback((refreshToken: string | null) => {
    clearRefreshTimer();

    refreshTimerRef.current = setTimeout(async () => {
      try {
        let accessToken: string;
        if (cognitoMode) {
          // Cognito SDK handles refresh internally
          const result = await authApi.refreshAccessToken();
          accessToken = result.accessToken;
        } else {
          // Local auth needs explicit refresh token
          if (!refreshToken) {
            clearAuthState();
            return;
          }
          const result = await authApi.refreshAccessTokenLocal(refreshToken);
          accessToken = result.accessToken;
        }
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        setState(prev => ({ ...prev, accessToken }));
        scheduleTokenRefresh(refreshToken);
      } catch (error) {
        console.error('Token refresh failed:', error);
        clearAuthState();
      }
    }, TOKEN_REFRESH_INTERVAL);
  }, [clearRefreshTimer, cognitoMode, clearAuthState]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const userJson = localStorage.getItem(STORAGE_KEYS.USER);

    if (accessToken && userJson) {
      try {
        const user = JSON.parse(userJson) as User;
        setState({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
        scheduleTokenRefresh(refreshToken);
        identifyUser(user.id);
      } catch (error) {
        console.error('Failed to restore auth state:', error);
        clearAuthState();
      }
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }

    return () => clearRefreshTimer();
  }, [scheduleTokenRefresh, clearAuthState, clearRefreshTimer]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login(email, password);

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));

    setState({
      user: response.user,
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      isAuthenticated: true,
      isLoading: false,
    });

    scheduleTokenRefresh(response.refreshToken);
    identifyUser(response.user.id);
    trackLogin();
  }, [scheduleTokenRefresh]);

  const register = useCallback(async (email: string, password: string, betaKey?: string) => {
    const response = await authApi.register(email, password, betaKey);
    trackRegisterCompleted(getStoredUtmParams());
    return { requiresVerification: response.requiresVerification };
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout(state.refreshToken);
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
    resetUser();
    clearAuthState();
  }, [state.refreshToken, clearAuthState]);

  const getAccessToken = useCallback(() => {
    return state.accessToken;
  }, [state.accessToken]);

  const value: AuthContextType = useMemo(() => ({
    ...state,
    login,
    register,
    logout,
    getAccessToken,
  }), [state, login, register, logout, getAccessToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
